import { AxiosError } from "axios";
import { getApi, putApi, postApi } from "./apis";
import { toast } from "react-toastify";

interface CellUpdate {
  harvestDetailId: number;
  fieldName: string;
  value: string;
}

interface UpdateHarvestDetailRequest {
  cellUpdates: CellUpdate[];
}

interface GetHarvestDetailsByYearAndLandlordsRequest {
  year: number;
  landlordIds: number[];
  cropIds?: number[];
  fieldIds?: number[];
}

const requestCache = new Map();

const cacheKey = (endpoint: string, params?: any) => {
  return `${endpoint}${params ? JSON.stringify(params) : ""}`;
};

const withCache = async (key: string, request: () => Promise<any>) => {
  if (requestCache.has(key)) {
    return requestCache.get(key);
  }

  const promise = request();
  requestCache.set(key, promise);

  try {
    const result = await promise;
    setTimeout(() => requestCache.delete(key), 2000);
    return result;
  } catch (error) {
    requestCache.delete(key);
    throw error;
  }
};

export const fetchHarvestDetailDropdownData = async (type: string) => {
  let endpoint = "";

  switch (type) {
    case "availableYears":
      endpoint = "CropYear/Get";
      break;
    case "availableLandlords":
      endpoint = "masterData/farms";
      break;
    case "availableCrops":
      endpoint = "revenue/crop-types";
      break;
    case "availableFields":
      endpoint = "harvestdetail/fields";
      break;
    default:
      throw new Error("Invalid dropdown type");
  }

  return withCache(cacheKey(endpoint), async () => {
    try {
      return await getApi(endpoint);
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data for ${type}: ${error.message}`);
      }
      throw new Error(
        `Failed to fetch data for ${type}: unknown error occurred`
      );
    }
  });
};

export const fetchHarvestFieldsByLandlords = async (landlordIds: number[]) => {
  const endpoint = `Field/GetFieldsBasedOnLandlordIds`;
  return withCache(cacheKey(endpoint, landlordIds), async () => {
    try {
      return await postApi(endpoint, { id: landlordIds });
    } catch (error: unknown) {
      if (error instanceof Error) {
        throw new Error(`Failed to fetch data for Fields: ${error.message}`);
      }
      throw new Error(
        `Failed to fetch data for Fields: unknown error occurred`
      );
    }
  });
};

export const filterHarvestDetailData = async (data: GetHarvestDetailsByYearAndLandlordsRequest) => {
  try {
    return await postApi("harvestdetail/filter", data);
  } catch (error: unknown) {
    if (error instanceof Error) {
      toast.error("No data please try again");
      throw new Error(`Failed to fetch data : ${error.message}`);
    } else {
      toast.error("No data please try again");
      throw new Error("Failed to fetch data : unknown error occurred");
    }
  }
};

export const putHarvestDetailApi = async (data: UpdateHarvestDetailRequest) => {
  const endpoint = `HarvestDetail/harvestdetails`;
  try {
    return await putApi(endpoint, data);
  } catch (error: unknown) {
    if (error instanceof AxiosError) {
      const errorMessage = error.response?.data?.errors?.join('; ') ||
                          error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message;
      throw new Error(errorMessage);
    }
    throw new Error("Failed to update harvest detail");
  }
};